import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded';
import { Card, CardContent } from '@ovComponents/2-component/card/card';
import { ImportCsvModal } from '@ovComponents/2-component/importCsvModal/importCsvModal';
import { PieChartSecurities } from '@ovComponents/3-pattern/pieChartSecurities/pieChartSecurities';
import { SecuritiesReviewDialog } from '@ovComponents/3-pattern/securitiesReviewDialog/securitiesReviewDialog';
import { SecuritiesSelectionTable } from '@ovComponents/3-pattern/securitiesSelectionTable/securitiesSelectionTable';
import { getHouseholdModelPortfolioPieChartProps } from '@ovComponents/4-module/widgets/householdModelPortfolio/householdModelPortfolio.allocation.utils';
import type { HouseholdModelPortfolioModel } from '@ovComponents/4-module/widgets/householdModelPortfolio/householdModelPortfolio.types';
import { Box, Grid, IconButton, Tooltip, Typography } from 'hephaestus/web';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocalization } from '../../../../../hooks/useLocalization';
import type { EditedHouseholdModelPortfolio } from '../householdModelPortfolioDraft/householdAllocations';
import { getInitializeAllocationTableOptions } from './initializeAllocationTableOptions';
import { useInitializeAllocationsEditor } from './useInitializeAllocationsEditor';

export type HouseholdModelPortfolioEditableAllocationProps = {
  model: HouseholdModelPortfolioModel;
  allowCsvImport?: boolean;
};

export const HouseholdModelPortfolioEditableAllocation = ({
  model,
  allowCsvImport = true,
}: HouseholdModelPortfolioEditableAllocationProps) => {
  const { translateBackendString } = useLocalization();
  const { t } = useTranslation(['components', 'modelPortfolios']);
  const [showImportCsvModal, setShowImportCsvModal] = useState(false);
  const tableOptions = useMemo(() => getInitializeAllocationTableOptions(), []);

  const {
    securitiesWithRanks,
    isAssetClassStrategy,
    assetClassLevel,
    groupedSecurities,
    unassignedSecurities,
    flushPendingSecurities,
    onAddSecurity,
    onAddCash,
    onUpdateSecurity,
    onRemoveSecurity,
    onUpdateTaxRank,
    onUpdateTargetPercentage,
    onReorderSecurities,
    importLoading,
    showReviewDialog,
    unrecognizedSecurities,
    availableSecurities,
    handleImportFromCsv,
    handleReviewSubmit,
    handleReviewClose,
  } = useInitializeAllocationsEditor({
    draftModel: model as EditedHouseholdModelPortfolio,
  });

  const pieChartProps = useMemo(
    () =>
      getHouseholdModelPortfolioPieChartProps(
        translateBackendString,
        model,
        securitiesWithRanks,
      ),
    [model, securitiesWithRanks],
  );

  return (
    <Box
      data-testid='household-initialize-editable-allocation'
      // Row changes are written to the workflow context on a debounce, and the
      // step reads that context to decide whether Initialize may proceed.
      // Leaving a field is the moment before the advisor reaches that button,
      // so the pending rows are landed here rather than racing the click.
      onBlurCapture={flushPendingSecurities}
      sx={{
        mt: 3,
      }}
    >
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 3.5 }}>
          <Card>
            <CardContent>
              <PieChartSecurities
                testId='household-initialize-allocation-pie-chart'
                variant='none'
                securities={pieChartProps.securities}
                modelPortfolioName={pieChartProps.modelPortfolioName}
                portfolioTemplate={pieChartProps.portfolioTemplate}
                assetClassLevel={pieChartProps.assetClassLevel}
                isAssetClassStrategy={isAssetClassStrategy}
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 8.5 }}>
          <Card>
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Typography
                  data-testid='household-initialize-allocation-title'
                  variant='headingSmall'
                >
                  {t('modelPortfolios:portfolio.tabs.assetAllocation')}
                </Typography>
                {allowCsvImport ? (
                  <Tooltip title={t('modelPortfolios:importCsv.uploadTooltip')}>
                    <IconButton
                      data-testid='household-initialize-import-csv-button'
                      size='xsmall'
                      aria-label='import csv'
                      disabled={importLoading}
                      onClick={() => setShowImportCsvModal(true)}
                    >
                      <UploadFileRoundedIcon fontSize='inherit' />
                    </IconButton>
                  </Tooltip>
                ) : null}
              </Box>
            </CardContent>

            <SecuritiesSelectionTable
              securities={securitiesWithRanks}
              options={tableOptions}
              onAddSecurity={onAddSecurity}
              onAddCash={onAddCash}
              onUpdateSecurity={onUpdateSecurity}
              onRemoveSecurity={onRemoveSecurity}
              onUpdateTaxRank={onUpdateTaxRank}
              onUpdateTargetPercentage={onUpdateTargetPercentage}
              onReorderSecurities={onReorderSecurities}
              groupedByAssetClass={
                isAssetClassStrategy && groupedSecurities
                  ? groupedSecurities
                  : undefined
              }
              unassignedSecurities={
                isAssetClassStrategy ? unassignedSecurities : undefined
              }
              securitySelectAssetClassLevel={
                isAssetClassStrategy ? assetClassLevel : undefined
              }
              ignoreBorderStyling
            />
          </Card>
        </Grid>
      </Grid>

      <ImportCsvModal
        open={showImportCsvModal}
        onClose={() => setShowImportCsvModal(false)}
        onImport={async (file, onProgress) => {
          await handleImportFromCsv(file);
          onProgress?.(100);
        }}
        testIdPrefix='household-initialize-allocation'
        importFileType='PORTFOLIO_SECURITIES'
        title={t('modelPortfolios:importCsv.modalTitle')}
        description={t('modelPortfolios:importCsv.modalDescription')}
        importButtonLabel={t('modelPortfolios:importCsv.import')}
        successMessage={t('modelPortfolios:importCsv.successMessage')}
        errorMessage={t('modelPortfolios:importCsv.errorMessage')}
      />

      <SecuritiesReviewDialog
        open={showReviewDialog}
        onClose={handleReviewClose}
        onSubmit={handleReviewSubmit}
        unrecognizedSecurities={unrecognizedSecurities}
        availableSecurities={availableSecurities}
        loading={importLoading}
      />
    </Box>
  );
};
